import Header from "@/components/Header";
import GridHero from "@/components/GridHero";
import ScrollReveal from "@/components/ScrollReveal";
import SEOMetaTags from "@/components/SEOMetaTags";
import StructuredData from "@/components/StructuredData";
import FAQSchema from "@/components/FAQSchema";
import { 
  LazyProducts, 
  LazyContact,
  ProductsSkeleton,
  ContactSkeleton
} from "@/components/LazyComponents";
import { Suspense } from "react";
import BrandWall from "@/components/BrandWall";
import BrandWallDifesa from "@/components/BrandWallDifesa";
import ProjectsGrid from "@/components/ProjectsGrid";
import MinimalFooter from "@/components/MinimalFooter";
import { usePublicMarchi, usePublicServizi } from "@/hooks/usePublicEntities";
import { useLanguage } from "@/contexts/LanguageContext";
import { sortDifesaBrands } from "@/utils/brandOrder";

// Map DB marchi to wall format
function mapMarchiToBrands(marchi: any[]) {
  return marchi.map(m => ({
    name: m.nome,
    category: m.tipo === 'difesa' ? 'Marchio della Difesa' : m.tipo === 'partner' ? 'Marchio Partner' : 'Marchio Fabbriche',
    slug: m.slug,
    logo: m.carousel_logo_url || m.logo_url || "",
    website: m.has_internal_page ? `/brands/${m.slug}` : (m.website_url || `/brands/${m.slug}`),
    heroImage: m.hero_image_url || null,
  }));
}

// Map service slug to a Lucide icon type used by BrandCarousel when useIcons=true
const SERVIZIO_ICON_MAP: Record<string, "private-label" | "hotellerie" | "ristorazione" | "regalistica" | "esperienze"> = {
  "private-label": "private-label",
  "hotellerie": "hotellerie",
  "ristorazione": "ristorazione",
  "regali-aziendali": "regalistica",
  "esperienze": "esperienze",
};

function mapServiziToProjects(servizi: any[]) {
  return servizi.map(s => ({
    name: s.nome,
    slug: s.slug,
    iconType: SERVIZIO_ICON_MAP[s.slug],
    description: s.descrizione || s.descrizione_breve || "",
    heroImage: s.hero_image_url || null,
  }));
}

const Index = () => {
  const { t } = useLanguage();
  const { data: marchi } = usePublicMarchi();
  const { data: servizi } = usePublicServizi();

  const nostriBrands = mapMarchiToBrands((marchi || []).filter(m => m.tipo === 'fabbriche' || m.tipo === 'partner'));
  const difesaBrands = mapMarchiToBrands(sortDifesaBrands((marchi || []).filter(m => m.tipo === 'difesa')));
  const progetti = mapServiziToProjects(servizi || []);

  const faqItems = [
    {
      question: "Cos'è Fabbriche Toscane?",
      answer: "Fabbriche Toscane è una piattaforma unica che unisce 5 fabbriche artigianali toscane: Cioccolateria, Biscottificio, Distilleria, Laboratorio Farmaceutico e Azienda Agricola. Offriamo prodotti di eccellenza che combinano tradizione e innovazione."
    },
    {
      question: "Quali sono i vostri marchi principali?",
      answer: "I nostri marchi propri includono La Signoria del Cioccolato, Novo Receptario Fiorentino, Distilleria Toscana, Nux Etrusca e Tuttobene. Produciamo anche a marchio della Difesa italiana."
    },
    {
      question: "Offrite servizi di private label?",
      answer: "Sì, offriamo servizi completi di private label per hotellerie, ristorazione, regalistica aziendale ed esperienze personalizzate."
    },
    {
      question: "Dove si trovano le vostre fabbriche?",
      answer: "Le nostre fabbriche si trovano in diverse località della Toscana, tra Firenze, la Maremma e altre zone storiche della regione."
    },
    {
      question: "Come posso ordinare i vostri prodotti?",
      answer: "Puoi contattarci tramite il form sul nostro sito o scrivendo a contatti@fabbrichetoscane.it per richiedere cataloghi e informazioni sugli ordini."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOMetaTags 
        title="Fabbriche Toscane | Eccellenze Artigianali Toscane"
        description="Cioccolateria, biscottificio, distilleria, laboratorio farmaceutico e azienda agricola in Toscana. Soluzioni HORECA e private label di qualità."
        keywords="fabbriche toscane, eccellenze artigianali, cioccolateria artigianale, biscottificio toscano, distilleria italiana, laboratorio farmaceutico, azienda agricola biologica, HORECA, private label, prodotti alimentari toscani"
        canonical="https://fabbrichetoscane.it/"
        ogImage="https://fabbrichetoscane.it/og-image.png"
        ogType="website"
        alternateLanguages={{
          'it': 'https://fabbrichetoscane.it/',
          'en': 'https://fabbrichetoscane.it/en/'
        }}
      />
      <FAQSchema faqs={faqItems} />
      <StructuredData type="organization" />
      <StructuredData type="website" />
      <StructuredData 
        type="breadcrumb" 
        breadcrumbs={[
          { name: "Home", url: "/" }
        ]} 
      />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <GridHero />
        <section className="container mx-auto px-6 pt-12 pb-4 text-center" aria-labelledby="home-heading">
          <h1 id="home-heading" className="font-display text-display-md md:text-display-lg text-foreground tracking-wide">
            Fabbriche Toscane
          </h1>
          <p className="mt-3 max-w-2xl mx-auto text-muted-foreground">
            Una piattaforma unica che unisce cinque fabbriche artigianali toscane — cioccolateria, biscottificio, distilleria, laboratorio farmaceutico e azienda agricola — per portare l'eccellenza del territorio sui mercati italiani e internazionali.
          </p>
        </section>
        <ScrollReveal>
          <BrandWall
            id="marchi"
            eyebrow="Fabbriche"
            title={t('pages.index.ourBrands')}
            description={t('pages.index.ourBrandsDesc')}
            brands={nostriBrands}
          />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <BrandWallDifesa
            id="marchi-difesa"
            eyebrow="Istituzionale"
            title={t('pages.index.defenseBrands')}
            description={t('pages.index.defenseBrandsDesc')}
            brands={difesaBrands}
          />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <ProjectsGrid
            id="progetti"
            eyebrow="Su misura"
            title={t('pages.index.projectsTitle')}
            description={t('pages.index.projectsDesc')}
            projects={progetti}
          />
        </ScrollReveal>
        <ScrollReveal>
          <Suspense fallback={<ProductsSkeleton />}>
            <LazyProducts />
          </Suspense>
        </ScrollReveal>
        <ScrollReveal>
          <Suspense fallback={<ContactSkeleton />}>
            <LazyContact />
          </Suspense>
        </ScrollReveal>
      </main>
      <MinimalFooter />
    </div>
  );
};

export default Index;
